// Hooks de mutaciones para autenticación con React Query
// Maneja las operaciones de login, logout y registro con Supabase
// Incluye mutaciones para gestión de perfil de usuario

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';
import { 
  verifyUserPassword, 
  verifyUserEmail, 
  updateUserEmail, 
  updateUserPassword, 
  deactivateUserAccount, 
  signOut 
} from '../services/authService';

// Hook para manejar el login de usuarios
export const useAuthMutations = () => {
  const queryClient = useQueryClient();

  // Mutación para login
  const loginMutation = useMutation({
    mutationFn: async ({ email, password, rememberMe = false }) => {
      // Guardamos la preferencia de "Recordarme" en localStorage
      localStorage.setItem('rememberMe', rememberMe.toString());
      
      // Si el usuario quiere ser recordado, guardamos el email
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        // Limpiamos el email guardado si no quiere ser recordado
        localStorage.removeItem('rememberedEmail');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      // Invalida y refresca la query de sesión
      queryClient.invalidateQueries(['auth', 'session']);
      toast.success('¡Inicio de sesión exitoso!');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al iniciar sesión');
    },
  });

  // Mutación para logout
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      // Limpia todas las queries relacionadas con autenticación
      queryClient.clear();
      toast.success('Sesión cerrada correctamente');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al cerrar sesión');
    },
  });

  // Mutación para registro de nuevos usuarios
  const registerMutation = useMutation({
    mutationFn: async ({ email, password, userData }) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData || {},
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      if (data.user && !data.session) {
        // Usuario creado pero necesita confirmación por email
        toast.success('Cuenta creada. Revisa tu email para confirmar.');
      } else if (data.session) {
        // Usuario creado y sesión iniciada (email auto-confirmado)
        queryClient.invalidateQueries(['auth', 'session']);
        toast.success('¡Cuenta creada e iniciada sesión!');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear cuenta');
    },
  });

  return {
    loginMutation,
    logoutMutation,
    registerMutation,
  };
};

/**
 * Hook para verificar la contraseña del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useVerifyPasswordMutation = (options = {}) => {
  return useMutation({
    mutationFn: verifyUserPassword,
    onSuccess: (data, variables) => {
      console.log(' --- useVerifyPasswordMutation(data)', data);
      // La verificación solo llega aquí si la contraseña es correcta
      console.log('Contraseña verificada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error verificando contraseña:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para verificar si un email ya está en uso
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useVerifyEmailMutation = (options = {}) => {
  return useMutation({
    mutationFn: verifyUserEmail,
    onSuccess: (data, variables) => {
      console.log('Verificación de email completada');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error verificando email:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para actualizar el email del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useUpdateEmailMutation = (options = {}) => {
  return useMutation({
    mutationFn: updateUserEmail,
    onSuccess: (data, variables) => {
      console.log('Email actualizado exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error actualizando email:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para actualizar la contraseña del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useUpdatePasswordMutation = (options = {}) => {
  return useMutation({
    mutationFn: updateUserPassword,
    onSuccess: (data, variables) => {
      console.log('Contraseña actualizada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error actualizando contraseña:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para desactivar la cuenta del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useDeactivateAccountMutation = (options = {}) => {
  return useMutation({
    mutationFn: deactivateUserAccount,
    onSuccess: (data, variables) => {
      console.log('Cuenta desactivada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error desactivando cuenta:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para cerrar sesión
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useSignOutMutation = (options = {}) => {
  return useMutation({
    mutationFn: signOut,
    onSuccess: (data, variables) => {
      console.log('Sesión cerrada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error cerrando sesión:', error);
      options.onError?.(error);
    },
    ...options
  });
};
